import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Spin, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { Plyr } from 'plyr-react';
import 'plyr-react/plyr.css';
import {
    GET_SPLIT_TUTORIAL_VIDEO_URL,
    GetSplitTutorialVideoUrlResponse,
    GetSplitTutorialVideoUrlVariables,
    SplitTutorialVideoType,
} from '../../graphql/getSplitTutorialVideoUrl';
import { toTutorialVideoLanguage } from '../../utils/tutorialVideoLanguage';
import { translateGraphQLError } from '../../../../../utils/graphqlUtils';

/**
 * 教学视频独立播放页（新标签打开）。
 * 用 Plyr 播放器，控制条不含下载/画中画，避免浏览器原生播放器的下载入口。
 * URL: /tutorial-video?type=ALGO|MAP&lang=zh|en|es（lang 缺省时取当前界面语言）
 */
const TutorialVideoPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const rawType = (searchParams.get('type') || '').toUpperCase();
    const videoType: SplitTutorialVideoType = rawType === 'MAP' ? 'MAP' : 'ALGO';
    const language = toTutorialVideoLanguage(searchParams.get('lang') || i18n.resolvedLanguage || i18n.language);

    const [url, setUrl] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const [fetchUrl] = useLazyQuery<GetSplitTutorialVideoUrlResponse, GetSplitTutorialVideoUrlVariables>(
        GET_SPLIT_TUTORIAL_VIDEO_URL,
        { fetchPolicy: 'network-only' },
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data, error } = await fetchUrl({ variables: { videoType, language } });
                if (cancelled) return;
                const u = data?.getSplitTutorialVideoUrl;
                if (u) {
                    setUrl(u);
                } else {
                    setErrorMsg(translateGraphQLError(error, 'splitOrder.drawer.videoGuideLoadFailed'));
                }
            } catch (e) {
                if (!cancelled) setErrorMsg(translateGraphQLError(e, 'splitOrder.drawer.videoGuideLoadFailed'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [videoType, language, fetchUrl]);

    const source = useMemo(
        () => (url ? { type: 'video' as const, sources: [{ src: url, type: 'video/mp4' }] } : null),
        [url],
    );

    // 控制条：去掉 download / pip / airplay，避免下载入口；autoplay 自动播放
    const options = useMemo(
        () => ({
            autoplay: true,
            controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
            settings: ['speed'],
        }),
        [],
    );

    return (
        <div
            className="w-screen h-screen flex items-center justify-center"
            style={{ background: '#000' }}
            onContextMenu={(e) => e.preventDefault()}
        >
            {loading ? (
                <Spin size="large" />
            ) : errorMsg ? (
                <Result status="warning" title={errorMsg} />
            ) : source ? (
                <div style={{ width: '100%', maxWidth: 1100, padding: 16 }}>
                    <Plyr source={source} options={options} />
                </div>
            ) : (
                <Result status="warning" title={t('splitOrder.drawer.videoGuideLoadFailed')} />
            )}
        </div>
    );
};

export default TutorialVideoPage;
